import { Fragment } from 'react';
import { List, Typography, useTheme } from '@mui/material';

import useI18n from 'hooks/useI18n';
import { INursingRecord } from 'apis/main/type';

import RecordTitle from './RecordTitle';
import ActionButtons from './ActionButtons';

interface Props extends INursingRecord {
  activeId?: number;
  studentNo: string | undefined;
  nurseName: string;
  refetch?: () => void;
}

const normalizeMultilineValue = (value: any): any => {
  if (typeof value === 'string') {
    return value
      .replace(/\r\n/g, '\n')
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n');
  }

  if (Array.isArray(value)) {
    return value.map(item => normalizeMultilineValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, item]) => {
      acc[key] = normalizeMultilineValue(item);
      return acc;
    }, {} as Record<string, any>);
  }

  return value;
};

const RecordItem = (props: Props) => {
  const { palette } = useTheme();
  const i18n = useI18n();
  const {
    activeId,
    studentNo,
    nurseName,
    create_at,
    content,
    nursing_record_id,
    record_type,
    refetch,
    ...otherProps
  } = props;

  const actionButtons = refetch ? (
    <ActionButtons
      {...otherProps}
      content={content}
      record_type={record_type}
      nursing_record_id={nursing_record_id}
      refetch={refetch}
    />
  ) : null;

  const titleProps = {
    actionButtons,
    studentNo: studentNo,
    nurseName: nurseName,
    create_at: create_at,
    record_time: props.record_time,
    title: i18n(`RECORD.${record_type}`),
  };

  const className = activeId === nursing_record_id ? 'active' : '';
  const contents = normalizeMultilineValue(JSON.parse(content));
  const type = i18n(`RECORD.TYPE.${record_type}`);
  const contentKeys = Object.keys(contents) as any[];
  const noSectionDividerKeys = [
    'sign_sx_etc',
    'omaha_target2',
    'omaha_target3',
    'omaha_target4',
    'omaha_knowledge',
    'omaha_behavior',
    'omaha_status',
  ];

  return (
    <List
      sx={{
        pt: 1,
        pb: 2,
        '& .MuiTypography-caption': {
          fontSize: '16px',
          lineHeight: 1.6,
        },
        '& br': {
          display: 'none',
        },
        '& li': {
          margin: 0,
        },
        '&.active': {
          backgroundColor: palette.grey[100],
        },
        px: 1,
      }}
      className={className}
      component="ul"
    >
      <RecordTitle {...titleProps} />
      {contentKeys.map((contentKey, idx: number) => {
        const innerDivider =
          idx > 0 && !noSectionDividerKeys.includes(contentKey) ? (
            <Typography
              component="li"
              className="record-divider"
              sx={{
                listStyle: 'none',
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                mt: '14px !important',
                mb: '14px !important',
                lineHeight: 0,
              }}
            />
          ) : null;

        if (contentKey === 'diagnosisRelate') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                fontWeight="bold"
                sx={{ mt: '8px !important', mb: '6px !important' }}
              >
                • 診斷
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {i18n(`${type}.${contentKey}` as any)}
              </Typography>
            </Fragment>
          );
        } else if (type === 'REMARKS' && contentKey === 'diagnosis') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'sign_sx') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                fontWeight="bold"
                sx={{ mt: '8px !important', mb: '6px !important' }}
              >
                • {i18n(`${type}.${contentKey}` as any)}
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey].map((sign: string, i: number) => {
                  if (i === contents[contentKey].length - 1) return sign;
                  return sign + ', ';
                })}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'sign_sx_etc') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '-16px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_target1') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                fontWeight="bold"
                sx={{ mt: '8px !important', mb: '6px !important' }}
              >
                • Intervention Scheme
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  fontSize: '16px',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                Teaching, Guidance, and Counseling —
                Targets and Client-Specific Information
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_target2') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  fontSize: '16px',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                Treatments and Procedures —
                Targets and Client-Specific Information
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_target3') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  fontSize: '16px',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                Case Management —
                Targets and Client-Specific Information
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_target4') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  fontSize: '16px',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                Surveillance —
                Targets and Client-Specific Information
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_knowledge_selct') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                fontWeight="bold"
                sx={{ mt: '18px !important', mb: '6px !important' }}
              >
                • Problem Rating Scale for Outcomes
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  fontSize: '16px',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                Knowledge
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_knowledge') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                - {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_behavior_selct') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  fontSize: '16px',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                Behavior
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_behavior') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                - {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_status_selct') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  fontSize: '16px',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                Status
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else if (contentKey === 'omaha_status') {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  color: '#0000008e',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                - {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        } else {
          return (
            <Fragment key={contentKey}>
              {innerDivider}
              <Typography
                component="li"
                variant="caption"
                fontWeight="bold"
                sx={{ mt: '8px !important', mb: '6px !important' }}
              >
                • {i18n(`${type}.${contentKey}` as any)}
              </Typography>
              <Typography
                component="li"
                variant="caption"
                sx={{
                  pl: 1,
                  whiteSpace: 'pre-wrap',
                  mt: '4px !important',
                  mb: '4px !important',
                }}
              >
                {contents[contentKey]}
              </Typography>
            </Fragment>
          );
        }
      })}
    </List>
  );
};

export default RecordItem;
